import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { LearningFlow } from "../../../domain/learning-flow/flow";
import { createFlowState, findFlowNode, reduceFlow } from "../../../domain/learning-flow/engine";
import { Icon } from "../../../design-system/primitives/Icon";
import styles from "./CaseWorkspace.module.css";

export function CaseWorkspace({ flow }: { readonly flow: LearningFlow }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("adim") ?? undefined;
  const [state, setState] = useState(() => createFlowState(flow, requested));
  const headingRef = useRef<HTMLHeadingElement>(null);
  const node = findFlowNode(flow, state.currentNodeId) ?? flow.nodes[0];
  const nodeIndex = Math.max(0, flow.nodes.findIndex((item) => item.id === node?.id));
  const progress = Math.round(((nodeIndex + 1) / flow.nodes.length) * 100);

  useEffect(() => { document.title = `${flow.title} | PedEmUP`; }, [flow.title]);
  useEffect(() => {
    if (requested && requested !== state.currentNodeId && findFlowNode(flow, requested)) setState(createFlowState(flow, requested));
  }, [flow, requested, state.currentNodeId]);
  useEffect(() => { headingRef.current?.focus({ preventScroll: true }); }, [state.currentNodeId]);

  const selectedChoice = useMemo(() => node ? state.selections[node.id] : undefined, [node, state.selections]);
  if (!node) return null;

  const apply = (action: Parameters<typeof reduceFlow>[2]) => {
    const next = reduceFlow(flow, state, action);
    if (next === state) return;
    setState(next);
    setSearchParams({ adim: next.currentNodeId });
  };

  return <article className={styles.workspace}>
    <header className={styles.header}>
      <div><p>Vaka ile Öğren</p><h1>{flow.title}</h1><span>{flow.summary}</span></div>
      <div className={styles.progress} aria-label={`Vaka ilerlemesi yüzde ${progress}`}><span style={{ width: `${progress}%` }} /></div>
    </header>
    <main className={`${styles.panel} ${styles[node.tone ?? "neutral"]}`}>
      <div className={styles.kicker}>{node.eyebrow}</div>
      <h2 ref={headingRef} tabIndex={-1}>{node.title}</h2>
      <div className={styles.body}>{node.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
      {node.choices && <fieldset className={styles.choices}><legend>Yanıtınızı seçin</legend>{node.choices.map((choice) => <button key={choice.id} type="button" aria-pressed={selectedChoice === choice.id} onClick={() => apply({ type: "choose", choiceId: choice.id })}><span>{choice.label}</span><Icon name="arrow" /></button>)}</fieldset>}
      <nav className={styles.actions} aria-label="Vaka kontrolleri">
        <button type="button" disabled={!state.history.length} onClick={() => apply({ type: "back" })}>← Geri</button>
        {node.next && <button className={styles.primary} type="button" onClick={() => apply({ type: "continue" })}>{node.kind === "feedback" ? "Sonraki bulgu" : "Devam"}<Icon name="arrow" /></button>}
        {state.completed && <button className={styles.primary} type="button" onClick={() => apply({ type: "restart" })}>Vakayı yeniden başlat</button>}
      </nav>
    </main>
    <aside className={styles.context}><strong>Bu vakada</strong><ul><li>Her karar sonrası gerekçeyi görün.</li><li>Yeni bulgu geldiğinde ilk çerçevenizi yeniden değerlendirin.</li><li>Olgu verileri eğitim amaçlıdır; hasta verisi saklanmaz.</li></ul></aside>
  </article>;
}
